import { useState, useRef } from "react";
import { ArrowRight, FileText, Loader2, UploadCloud, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STAGES = [
  {
    id: "student",
    icon: "◎",
    label: "Building My Foundation",
    desc: "Final year, recent graduate, or entering the workforce",
  },
  {
    id: "professional",
    icon: "⬡",
    label: "Established Professional",
    desc: "Working in your field with 3+ years of experience",
  },
  {
    id: "pivot",
    icon: "✦",
    label: "Changing Direction",
    desc: "Experienced professional pivoting to a new field or role",
  },
];

const MAX_MB = 5;
const ACCEPTED_EXT = ["pdf", "docx"];

export default function InterviewIntake({ interviewId, onComplete }) {
  const auth = useAuth();
  const [stage, setStage] = useState(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [mismatch, setMismatch] = useState(null); // {detected, userSelected}
  const [resumeAnchors, setResumeAnchors] = useState(null); // factual_anchors after upload
  const fileRef = useRef(null);

  const firstName = auth.user?.name?.split(" ")[0] || "there";

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      setError("Please upload a PDF or Word (.docx) file.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`File must be under ${MAX_MB} MB.`);
      return;
    }
    setError("");
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleContinue = async () => {
    if (!stage) return;
    setProcessing(true);
    setError("");
    try {
      if (file) {
        const form = new FormData();
        form.append("file", file);
        form.append("career_stage", stage);
        const { data } = await auth.client.post(
          `/interviews/${interviewId}/upload-resume`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        // Surface a gentle note if the resume agent strongly disagrees with the user's choice
        if (
          data.detection_confidence === "high" &&
          data.detected_career_stage &&
          data.detected_career_stage !== stage
        ) {
          setMismatch({ detected: data.detected_career_stage, userSelected: stage });
          setProcessing(false);
          return;
        }
        // Show resume acknowledgment before proceeding
        if (data.factual_anchors && (data.factual_anchors.recent_role || data.factual_anchors.career_arc)) {
          setResumeAnchors(data.factual_anchors);
          setProcessing(false);
          return;
        }
      } else {
        await auth.client.patch(`/interviews/${interviewId}/career-stage`, {
          career_stage: stage,
        });
      }
      onComplete();
    } catch (e) {
      const det = e?.response?.data?.detail;
      setError(typeof det === 'string' ? det : Array.isArray(det) ? det.map(d => d.msg || String(d)).join('. ') : "Something went wrong. You can continue — your career stage has been noted.");
      // Still allow continuing even if resume processing failed
      if (!file || e.response?.status >= 500) {
        try {
          await auth.client.patch(`/interviews/${interviewId}/career-stage`, {
            career_stage: stage,
          });
          onComplete();
          return;
        } catch {}
      }
      setProcessing(false);
    }
  };

  const resolveMismatch = async (chosenStage) => {
    setProcessing(true);
    setMismatch(null);
    try {
      if (chosenStage !== stage) {
        await auth.client.patch(`/interviews/${interviewId}/career-stage`, {
          career_stage: chosenStage,
        });
      }
      onComplete();
    } catch {
      onComplete(); // proceed regardless
    }
  };

  const stageLabel = (id) => STAGES.find((s) => s.id === id)?.label ?? id;

  return (
    <div className="briefing-shell" data-testid="interview-intake">
      <p className="briefing-kicker" data-testid="intake-kicker">
        Before we begin
      </p>

      <div className="briefing-title" data-testid="intake-title">
        <h2>Tell us where you are.</h2>
        <p>
          We'll shape the interview to your stage — the questions, the depth,
          the frame.
        </p>
      </div>

      {/* Career stage selector */}
      <div
        className="intake-stage-grid"
        role="radiogroup"
        aria-label="Career stage"
        data-testid="career-stage-grid"
      >
        {STAGES.map((s) => (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={stage === s.id}
            className={`intake-stage-card${stage === s.id ? " selected" : ""}`}
            onClick={() => setStage(s.id)}
            data-testid={`career-stage-${s.id}`}
          >
            <span className="intake-stage-icon" aria-hidden="true">
              {s.icon}
            </span>
            <strong className="intake-stage-label">{s.label}</strong>
            <p className="intake-stage-desc">{s.desc}</p>
          </button>
        ))}
      </div>

      <hr className="intake-divider" aria-hidden="true" />

      {/* Resume upload */}
      <div className="intake-upload-section" data-testid="resume-upload-section">
        <p className="intake-upload-label">
          Resume or CV{" "}
          <span>Optional — we use it as a starting point, not a script.</span>
        </p>

        {file ? (
          <div className="intake-file-chip" data-testid="selected-file-chip">
            <FileText size={18} />
            <span title={file.name}>{file.name}</span>
            <button
              type="button"
              className="intake-file-remove"
              onClick={() => {
                setFile(null);
                setError("");
              }}
              aria-label="Remove file"
              data-testid="remove-file-button"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div
            className={`intake-upload-zone${dragOver ? " drag-over" : ""}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              fileRef.current?.click()
            }
            aria-label="Upload resume"
            data-testid="upload-zone"
          >
            <UploadCloud size={30} />
            <p>Drop your resume here or click to browse</p>
            <small>PDF or Word (.docx) · Max {MAX_MB} MB</small>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="resume-file-input"
        />
      </div>

      {error && (
        <p
          className="error-note"
          style={{ maxWidth: "640px", width: "100%" }}
          data-testid="intake-error"
        >
          {error}
        </p>
      )}

      {resumeAnchors && (
        <div
          className="info-note"
          style={{ maxWidth: "640px", width: "100%" }}
          data-testid="resume-anchors-note"
        >
          <p style={{ marginBottom: ".5rem" }}>
            Got it, <strong>{firstName}</strong>. Here's what we'll carry into the interview:
          </p>
          {resumeAnchors.recent_role && (
            <p style={{ marginBottom: ".25rem" }}>
              <span className="mono-label" style={{ fontSize: ".72rem" }}>RECENT ROLE</span>{" "}
              {resumeAnchors.recent_role}
            </p>
          )}
          {resumeAnchors.career_arc && (
            <p style={{ marginBottom: ".25rem", color: "var(--text-secondary, #666)" }}>
              {resumeAnchors.career_arc}
            </p>
          )}
          {resumeAnchors.tech_stack?.length > 0 && (
            <p style={{ marginBottom: ".5rem", fontSize: ".82rem", color: "var(--text-secondary, #666)" }}>
              {resumeAnchors.tech_stack.slice(0, 6).join(" · ")}
            </p>
          )}
          <p style={{ fontSize: ".8rem", opacity: 0.6, marginBottom: ".75rem" }}>
            We won't repeat this back — it's silent background.
          </p>
          <button
            type="button"
            className="primary-action compact"
            onClick={onComplete}
            data-testid="resume-anchors-continue-button"
          >
            Looks right — begin interview <ArrowRight size={16} />
          </button>
        </div>
      )}

      {mismatch && (
        <div
          className="info-note"
          style={{ maxWidth: "640px", width: "100%" }}
          data-testid="stage-mismatch-note"
        >
          <p>
            Your resume suggests <strong>{stageLabel(mismatch.detected)}</strong>, but you
            selected <strong>{stageLabel(mismatch.userSelected)}</strong>. Which framing fits
            better? The interview questions will differ.
          </p>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              className="ghost-action small"
              onClick={() => resolveMismatch(mismatch.userSelected)}
              data-testid="mismatch-keep-button"
            >
              Keep: {stageLabel(mismatch.userSelected)}
            </button>
            <button
              type="button"
              className="primary-action compact"
              style={{ fontSize: ".82rem" }}
              onClick={() => resolveMismatch(mismatch.detected)}
              data-testid="mismatch-switch-button"
            >
              Switch to: {stageLabel(mismatch.detected)}
            </button>
          </div>
        </div>
      )}

      <div className="briefing-cta-group" style={resumeAnchors ? { display: "none" } : {}}>
        <button
          className="primary-action"
          onClick={handleContinue}
          disabled={!stage || processing}
          data-testid="intake-continue-button"
        >
          {processing ? (
            <>
              <Loader2 size={17} className="spin" />
              {file ? "Analysing your resume…" : "Preparing interview…"}
            </>
          ) : (
            <>
              Continue <ArrowRight size={18} />
            </>
          )}
        </button>
        {!stage && (
          <p className="briefing-time-note" data-testid="intake-hint">
            Select your stage above to continue
          </p>
        )}
      </div>
    </div>
  );
}
